import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { useUsers } from "@/api/queries/users";
import { useAgents } from "@/api/queries/agents";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { EntityLink } from "@/components/EntityLink";
import { relativeTime } from "@/lib/relativeTime";
import { CreateUserDialog } from "@/components/user-actions/CreateUserDialog";

const ROW_LIMIT = 50;

export function UsersList() {
  const { data, isLoading, isError, error, refetch } = useUsers();
  const agentsQuery = useAgents();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Supervisor-IDs → Email auflösen (Cross-Link); Agent-Anzahl pro Owner.
  const userMap = new Map((data ?? []).map((u) => [u.id, u]));
  const ownedCount = new Map<string, number>();
  for (const a of agentsQuery.data ?? []) {
    if (a.ownerId) ownedCount.set(a.ownerId, (ownedCount.get(a.ownerId) ?? 0) + 1);
  }
  const q = search.trim().toLowerCase();
  const filtered = (data ?? []).filter((u) => !q || `${u.email} ${u.id}`.toLowerCase().includes(q));
  const rows = filtered.slice(0, ROW_LIMIT);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">{data?.length ?? 0} Plattform-Accounts</p>
        <Button onClick={() => setCreateOpen(true)} data-testid="action-create-user">
          <Plus className="h-4 w-4" />
          Neuer User
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] max-w-[360px] flex-1">
          <Search className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="User suchen (Email / ID)…"
            data-testid="list-search"
            className="h-9 w-full rounded-lg border border-border bg-surface pl-[34px] pr-3 text-[13px] text-text outline-none placeholder:text-text3 focus:border-accent-border"
          />
        </div>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && filtered.length === 0}
        emptyMessage={data && data.length > 0 ? "Kein User passt zur Suche" : "Noch keine Users"}
        emptyHint={data && data.length > 0 ? "Suche anpassen" : "Klick 'Neuer User' zum Anlegen"}
        onRetry={() => refetch()}
        rowLimit={ROW_LIMIT}
        totalRows={filtered.length}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Rolle</TableHead>
              <TableHead>Supervisor</TableHead>
              <TableHead>Gitea</TableHead>
              <TableHead className="text-right">Agents</TableHead>
              <TableHead className="text-right">Erstellt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-clickable="true" onClick={() => navigate(`/users/${u.id}`)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface3 text-[12px] font-bold text-text2">
                      {u.email.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="mono font-medium text-text">{u.email}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={u.role === "admin" ? "enrolled" : "default"}>{u.role}</Badge>
                </TableCell>
                <TableCell>
                  <EntityLink
                    to={`/users/${u.supervisorId}`}
                    id={u.supervisorId}
                    label={userMap.get(u.supervisorId ?? "")?.email}
                    isResolving={isLoading}
                  />
                </TableCell>
                <TableCell>
                  {u.giteaUsername ? <span className="mono text-[12.5px] text-text2">{u.giteaUsername}</span> : <span className="text-text3">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <span className="tnum text-[13px] font-semibold text-text">{ownedCount.get(u.id) ?? 0}</span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-[12.5px] text-text3">{relativeTime(u.createdAt)}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>
      {createOpen && <CreateUserDialog open onOpenChange={setCreateOpen} />}
    </div>
  );
}
